import { Component, OnInit, OnDestroy, Input, ViewChild, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AlertService } from './alert.service';
import { Alert } from 'src/app/models/alert';
import { MatTableDataSource } from '@angular/material/table';
import { TenantState } from 'src/app/store/state/tenant.state';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { Select, Store } from '@ngxs/store';
import { Observable, ReplaySubject, Subject, combineLatest, finalize, take, takeUntil, debounceTime, filter, switchMap, tap, of } from 'rxjs';
import { DynamicTableInterface } from 'src/app/models/interface/dynamicTable';
import { TranslateService } from '@ngx-translate/core';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { FeatureSettings, TenantFeatures, isFeatureEnabled } from 'src/app/models/featureSettings';
import { canonicalQuery, parseQuery } from 'src/app/shared/saved-filters/saved-filters';
import { countActive, drawerOpenFor, setDrawerOpen } from 'src/app/shared/filter-drawer/filter-drawer.component';
import { SeverityTypes } from 'src/app/models/severityTypes';
import { CaseUserStatus } from 'src/app/models/referenceData';
import { CaseStatusGroup, groupCaseStatuses } from './case-status-groups';
import { TenantUser } from 'src/app/models/tenantUser';
import { FilterDropdownOption } from 'src/app/shared/components/filter-dropdown/filter-dropdown.component';
import { DateRangeFilterValue, EMPTY_DATE_RANGE, normalizeDateRange } from 'src/app/shared/components/date-range-filter/date-range-value';
import { AlertStatusType } from 'src/app/models/constant/alertStatusType';
import { UpdatePagedList } from 'src/app/store/actions/tenant.actions';
import { PagedList } from 'src/app/models/pagedList';
import { TableButton } from 'src/app/models/tableButton';
import { AlertCaseComponent } from '../alert-case/alert-case.component';
import { DrawerService } from 'src/app/shared/drawer/drawer.service';
import { DrawerOptions } from 'src/app/shared/drawer/models/drawer-options';
import { AlertruleService } from '../alertrule/alertrule.service';
import { GroupService } from 'src/app/fleet/group/group.service';
import { VehicleService } from 'src/app/fleet/vehicle/service/vehicle.service';
import { Group } from 'src/app/models/group';
import { NotificationPreferences } from 'src/app/models/notificationPreferences';
import { SpaRoutingService } from 'src/app/shared/services/spa.routing.service';
import { UserService } from 'src/app/users/user.service';
import { alertsTableDialogData } from './alerts-table-dialog-data';
import { AlertTriageOpener } from 'src/app/service-center/alert-detail-dialog/alert-triage-opener';
import { ConfigurationManageLinksService } from 'src/app/configuration/manage-link/configuration-manage-links.service';
import { TableFunction } from 'src/app/dynamic-table/dynamic-table.model';
import { findShopGroup } from 'src/app/shared/handler/common-functions.util';
import { AuthService } from 'src/app/services/auth.service';
import { AuthorizationService } from 'src/app/services/authorization.service';
import { MaintainVehicle } from 'src/app/models/constant/right';

@Component({
  selector: 'app-alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.css'],
  standalone: false
})
export class AlertComponent implements OnInit, OnDestroy, DynamicTableInterface {

  @SelectSnapshot(TenantState.getCurrentTenantExternalId) getCurrentTenantExternalId: string;
  @SelectSnapshot(TenantState.getPagedList) getPagedList: (url: string) => PagedList[];
  // The saved "My View" (alert categories) — the single scope source sent as AllowedTags.
  @SelectSnapshot(TenantSessionState.getCurrentUser) currentUser: TenantUser;
  @Select(TenantSessionState.getTenantFeatures) getTenantFeatures$: Observable<FeatureSettings>;
  @Select(TenantSessionState.getCurrentTenantUsers) currentTenantUsers$: Observable<TenantUser[]>;
  // Hydrated-user gate: the initial grid/summary loads must wait for this to emit a non-null
  // user so the saved My View (alertTags) is included in the very first request.
  @Select(TenantSessionState.getCurrentUser) currentUser$: Observable<TenantUser>;
  @ViewChild('groupExpandCell') groupExpandCellTemplate: TemplateRef<any>;

  private destroy$ = new Subject<void>();
  private featuresSub$ = new Subject<void>();
  // Fires when case statuses load; the initial summary waits so its chips have labels.
  private statusesLoaded$ = new ReplaySubject<void>(1);
  // Fires after applyTenantFeatures resolves grouped/flat mode; the initial load gates on
  // this (not the raw feature stream) so the first fetch uses the right mode.
  private featuresApplied$ = new ReplaySubject<void>(1);
  // switchMap funnels: a newer request cancels the in-flight one so a stale response can't
  // resolve last and overwrite the grid.
  private dataRequest$ = new Subject<void>();
  private summaryRequest$ = new Subject<void>();

  loading = true;
  loadingSummary = false;
  dataSource: MatTableDataSource<Alert>;
  tenantId: string;
  private currentSortBy = 'CreatedDate';
  private currentSortDescending = true;
  private currentOffset = 0;
  private currentLimit = 10;
  includeSelect = false;
  length: number;

  @Input() vin: string;
  @Input() isNested: boolean;

  selectedStatuses: number[] = [];
  selectedAlertSeverities: string[] = [];
  selectedVehicleSeverities: number[] = [];
  selectedLastUpdatedByUserIds: string[] = [];
  selectedAssignedUserIds: string[] = [];
  selectedAssignedLocationIds: string[] = [];
  selectedAlertRuleIds: string[] = [];
  viewUnreadOnly: boolean = false;

  searchForm: FormGroup;

  filterVin: string = '';

  caseStatuses: any[] = [];
  caseStatusLead: any[] = [];
  caseStatusGroups: CaseStatusGroup<any>[] = [];
  baselineSummary: any = null;
  unassignedUser: TenantUser = { id: '0', firstName: '', lastName: '', email: '', phoneNumber: '', createDateTimeUtc: new Date(), updateDateTimeUtc: new Date(), isDeactivated: false, groups: [], canReceiveSms: false, notificationPreferences: new NotificationPreferences() };
  tenantUsers: TenantUser[] = [];
  realTenantUsers: TenantUser[] = [];
  alertRules: any[] = [];
  locationGroups: any[] = [];
  // Filter-dropdown { value, label } options, rebuilt when their source arrays
  // load. Held as stable references so the OnPush dropdowns only re-check when
  // the list actually changes (not on every parent change-detection cycle).
  alertRuleOptions: FilterDropdownOption[] = [];
  assignedUserOptions: FilterDropdownOption[] = [];
  assignedLocationOptions: FilterDropdownOption[] = [];
  // Home Locations dropdown: the user's location groups, filtered by group id.
  homeLocationOptions: FilterDropdownOption[] = [];
  selectedLocationGroupIds: string[] = [];
  // Location dropdown: the user's shop groups plus the Over the Road pseudo-option.
  shopLocationOptions: FilterDropdownOption[] = [];
  selectedShopLocationIds: string[] = [];
  private readonly overTheRoadValue = 'OverTheRoad';
  createdRange: DateRangeFilterValue = EMPTY_DATE_RANGE;
  savedFiltersEnabled = false;
  filterDrawerEnabled = false;
  get drawerOpen(): boolean { return drawerOpenFor('alerts'); }
  set drawerOpen(open: boolean) { setDrawerOpen('alerts', open); }
  lastUpdatedByOptions: FilterDropdownOption[] = [];
  totalFilteredCount: number = 0;

  // Grouped Alerts (GroupedAlerts feature flag)
  hasGroupedAlertsEnabled = false;
  groupedView = false;
  // Tracks whether the user explicitly persisted a grouped/flat preference,
  // so the flag-on default (grouped) only applies on first visit.
  private groupedViewPersisted = false;

  alertSeverities = [
    { label: '@critical', value: 'Critical', count: 0, baselineCount: 0 },
    { label: '@high', value: 'High', count: 0, baselineCount: 0 },
    { label: '@medium', value: 'Medium', count: 0, baselineCount: 0 },
    { label: '@low', value: 'Low', count: 0, baselineCount: 0 },
    { label: '@none', value: 'None', count: 0, baselineCount: 0 }
  ];

  vehicleSeverities = [
    { label: '@critical', value: SeverityTypes.Critical, count: 0, baselineCount: 0 },
    { label: '@high', value: SeverityTypes.High, count: 0, baselineCount: 0 },
    { label: '@medium', value: SeverityTypes.Medium, count: 0, baselineCount: 0 },
    { label: '@low', value: SeverityTypes.Low, count: 0, baselineCount: 0 },
    { label: '@none', value: SeverityTypes.None, count: 0, baselineCount: 0 }
  ];

  showMoreFilters = false;

  readonly severityColorMap: Record<string, string> = {
    'Critical': '#f44336',
    'High': 'orange',
    'Medium': 'gold',
    'Low': '#009cdc'
  };

  readonly severityDescMap: Record<string, string> = {
    'Critical': '@severityDescCritical',
    'High': '@severityDescHigh',
    'Medium': '@severityDescMedium',
    'Low': '@severityDescLow'
  };

  private readonly quickFilterStatusLabels = new Set(['Follow Up Required', 'In For Repair']);

  vehicleSeverityOptions = [
    { displayName: 'Critical', id: SeverityTypes.Critical },
    { displayName: 'High', id: SeverityTypes.High },
    { displayName: 'Medium', id: SeverityTypes.Medium },
    { displayName: 'Low', id: SeverityTypes.Low },
    { displayName: 'None', id: SeverityTypes.None }
  ];

  private readonly allGridDisplayColumns = [
    { display: '', property: 'isRead', type: 'viewLog', noSort: true },
    { display: '@case', property: 'caseButton', type: 'button', noSort: true },
    { display: '@assetName', property: 'vehicleName', type: 'text', sortProperty: 'VehicleName' },
    { display: '@alertName', property: 'alertRule.subject', type: 'nestedText', sortProperty: 'Subject' },
    { display: '@severity', property: 'severity.value', type: 'severity', sortProperty: 'Severity' },
    { display: '@warningLamps', property: 'vehicleState.dashboard', type: 'dashboard', noSort: true },
    { display: '@createdDate', property: 'createDateTimeUtc', type: 'timeago', sortProperty: 'CreatedDate' },
    { display: '@lastUpdated', property: 'updateDateTimeUtc', type: 'timeago', sortProperty: 'UpdatedDate' },
    { display: '@vehicleSeverity', property: 'vehicleSeverity', type: 'severity', noSort: true, hidden: true },
    { display: '@caseStatus', property: 'caseUserStatus', type: 'text', noSort: true },
    { display: '@lastUpdatedBy', property: 'lastUpdatedBy', type: 'text', noSort: true, hidden: true },
    { display: '@assignedTo', property: 'assignedTo', type: 'text', noSort: true },
    { display: '@assignedToLocation', property: 'assignedToLocation', type: 'text', noSort: true, hidden: true },
    { display: '@homeLocations', property: 'locationGroups', type: 'chips', noSort: true },
    { display: '@shopLocations', property: 'vehicleLocation', type: 'text', noSort: true },
    { display: '@groups', property: 'displayGroups', type: 'chips', noSort: true, hidden: true }
  ];

  // Grouped-only trailing cell that combines the "+N" count and the expand
  // chevron in one minimal column. Rendered via the groupExpandCell template so
  // it shows both and toggles the children on click (stopPropagation → no row nav).
  private readonly groupExpandColumn = { display: '@occurences', property: 'expandCell', type: 'template', noSort: true };

  // Representative (parent) rows from the most recent grouped fetch — the source
  // of truth that rebuildGroupedRows flattens (parent + cached children when
  // expanded) into dataSource.data.
  private groupRows: any[] = [];

  gridDisplayColumns = [...this.allGridDisplayColumns];

  private activeRow: Alert = null;
  private dataLoaded = false;

  tableFunctions: TableFunction[] = [
    { display: '@showUnreadOnly', action: 'toggleUnreadOnly', matIcon: 'mark_email_unread' },
    { display: '@showAllColumns', action: 'show', icon: 'fa fa-eye' }
  ];
  hasCaseCreationEnabled: boolean;
  // The API refuses a case create without MaintainVehicle, so the list offers it only to those users.
  canCreateCase = false;
  // DiagnosticAlertModal feature flag: row clicks open the alert detail dialog
  // instead of navigating to the alert detail page.
  hasAlertModalEnabled = false;
  // HideAlertSeverityFilter feature flag (opt-out): hides the severity summary cards.
  hideSeverityFilter = false;

  constructor(
    private readonly alertService: AlertService,
    private router: Router,
    public translate: TranslateService,
    private store: Store,
    private drawerService: DrawerService,
    private alertRuleService: AlertruleService,
    private groupService: GroupService,
    private fb: FormBuilder,
    private spaRoutingService: SpaRoutingService,
    private userService: UserService,
    private readonly manageLinks: ConfigurationManageLinksService,
    private triage: AlertTriageOpener,
    private authService: AuthService,
    private authorizationService: AuthorizationService,
  ) {
    this.searchForm = this.fb.group({
      nameOrVinSearch: ['']
    });
    this.tableFunctions.push(...this.manageLinks.buildTableFunctions(['alertRules', 'faultCodes']));
  }

  ngOnInit(): void {
    if (this.vin) {
      this.filterVin = this.vin;
    }

    this.canCreateCase = this.authorizationService.hasRight(this.authService.user?.profile, MaintainVehicle);

    this.setupRequestStreams();

    this.triage.linkedAlertId$.pipe(takeUntil(this.destroy$)).subscribe(() => this.openAlertFromUrl());

    this.searchForm.valueChanges.pipe(
      debounceTime(400),
      takeUntil(this.destroy$)
    ).subscribe(() => this.refreshView());

    this.tenantId = this.getCurrentTenantExternalId;
    this.initializeTenantData();
  }

  private setupRequestStreams(): void {
    this.dataRequest$.pipe(
      switchMap(() => {
        this.loading = true;
        const useGrouped = this.groupedView && this.hasGroupedAlertsEnabled;
        return (useGrouped ? this.fetchGroupedData$() : this.fetchFlatData$())
          .pipe(finalize(() => this.loading = false));
      }),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.dataLoaded = true;
      this.openAlertFromUrl();
    });

    this.summaryRequest$.pipe(
      switchMap(() => {
        this.loadingSummary = true;
        return this.fetchSummary$().pipe(finalize(() => this.loadingSummary = false));
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  private syncUnreadToggleFunction(): void {
    const fn = this.tableFunctions.find(f => f.action === 'toggleUnreadOnly');
    if (fn) {
      fn.display = this.viewUnreadOnly ? '@showAll' : '@showUnreadOnly';
      fn.matIcon = this.viewUnreadOnly ? 'drafts' : 'mark_email_unread';
      this.tableFunctions = [...this.tableFunctions];
    }
  }

  private initializeTenantData(): void {
    this.resetFilterState();
    this.restoreFilterState();
    this.syncUnreadToggleFunction();
    this.loadTenantFeaturesAndUsers();
    this.loadStatuses();
    // Load once, after all three resolve: user (My View scope), features (grouped/flat mode),
    // statuses (chip labels).
    combineLatest([
      this.currentUser$.pipe(filter(u => !!u)),
      this.featuresApplied$,
      this.statusesLoaded$
    ]).pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        this.getData();
        this.getSummary();
      });
    this.loadAlertRules();
    this.loadLocationGroups();
    this.loadShopGroups();
  }

  private resetFilterState(): void {
    this.selectedAlertSeverities = [];
    this.selectedStatuses = [];
    this.selectedVehicleSeverities = [];
    this.selectedLastUpdatedByUserIds = [];
    this.selectedAssignedUserIds = [];
    this.selectedAssignedLocationIds = [];
    this.selectedLocationGroupIds = [];
    this.selectedShopLocationIds = [];
    this.selectedAlertRuleIds = [];
    this.viewUnreadOnly = false;
    this.clearReactiveForm();
  }

  private restoreFilterState(): void {
    const savedPathName = location.pathname + '/diagnostics/alert';
    const currentLists = this.getPagedList(savedPathName);
    const existingList = currentLists.find((x: PagedList) => x.url === savedPathName);
    if (!existingList) return;

    // Restore pagination and sort — re-derive structured sort params from saved mat sort state
    this.currentLimit = existingList.pageSize ?? this.currentLimit;
    this.currentOffset = (existingList.pageIndex ?? 0) * this.currentLimit;
    this.resolveStructuredSort(existingList.matSortActive, existingList.matSortDirection);

    // Restore filter card selections
    if (!existingList.customFilters) return;
    const filters = JSON.parse(existingList.customFilters);
    this.applyFacets(filters);
    if (filters.groupedView !== undefined) {
      this.groupedView = filters.groupedView;
      this.groupedViewPersisted = true;
    }

    this.showMoreFilters = this.hasMoreFiltersActive;
  }

  // Missing facets clear, so a saved filter replaces the current one rather than merging into it.
  private applyFacets(filters: Record<string, any>): void {
    this.selectedAlertSeverities = filters.selectedAlertSeverities ?? [];
    this.selectedStatuses = filters.selectedStatuses ?? [];
    this.selectedVehicleSeverities = filters.selectedVehicleSeverities ?? [];
    this.selectedLastUpdatedByUserIds = filters.selectedLastUpdatedByUserIds ?? [];
    this.selectedAssignedUserIds = filters.selectedAssignedUserIds ?? [];
    this.selectedAssignedLocationIds = filters.selectedAssignedLocationIds ?? [];
    this.selectedLocationGroupIds = filters.selectedLocationGroupIds ?? [];
    this.selectedShopLocationIds = filters.selectedShopLocationIds ?? [];
    this.selectedAlertRuleIds = filters.selectedAlertRuleIds ?? [];
    this.viewUnreadOnly = filters.viewUnreadOnly ?? false;
    this.createdRange = normalizeDateRange({
      from: filters.createdFrom,
      to: filters.createdTo,
      presetDays: filters.dateRangePresetDays
    });
    this.searchForm.patchValue({
      nameOrVinSearch: filters.nameOrVinSearch || ''
    }, { emitEvent: false });
  }

  // A preset range is stored as its day count, so it stays relative to the day it is applied.
  private facets() {
    const { from, to, presetDays } = this.createdRange;
    return {
      selectedAlertSeverities: this.selectedAlertSeverities,
      selectedStatuses: this.selectedStatuses,
      selectedVehicleSeverities: this.selectedVehicleSeverities,
      selectedLastUpdatedByUserIds: this.selectedLastUpdatedByUserIds,
      selectedAssignedUserIds: this.selectedAssignedUserIds,
      selectedAssignedLocationIds: this.selectedAssignedLocationIds,
      selectedLocationGroupIds: this.selectedLocationGroupIds,
      selectedShopLocationIds: this.selectedShopLocationIds,
      selectedAlertRuleIds: this.selectedAlertRuleIds,
      viewUnreadOnly: this.viewUnreadOnly,
      dateRangePresetDays: presetDays,
      createdFrom: presetDays == null ? from?.toISOString() ?? null : null,
      createdTo: presetDays == null ? to?.toISOString() ?? null : null,
      nameOrVinSearch: ((this.searchForm.value.nameOrVinSearch as string) ?? '').trim(),
    };
  }

  get savedQuery(): string {
    return canonicalQuery(this.facets());
  }

  // An absolute range ages past the 30-day floor; read it clamped as applying it will be, so it matches once applied.
  readonly readSavedQuery = (stored: string): string => {
    const saved = parseQuery(stored);
    if (!saved.createdFrom || saved.dateRangePresetDays != null) return stored;
    const { from } = normalizeDateRange({ from: saved.createdFrom as string });
    return JSON.stringify({ ...saved, createdFrom: from!.toISOString() });
  };

  applySavedFilter(query: string): void {
    this.applyFacets(parseQuery(query));
    this.syncUnreadToggleFunction();
    this.refreshView();
  }

  get drawerCounts(): { locations: number; alert: number; case: number; total: number } {
    const locations = countActive(this.selectedLocationGroupIds.length, this.selectedShopLocationIds.length);
    const alert = countActive(this.selectedAlertSeverities.length, this.selectedAlertRuleIds.length, this.hasDateRange);
    const cases = countActive(this.selectedStatuses.length, this.selectedAssignedUserIds.length, this.selectedLastUpdatedByUserIds.length, this.selectedAssignedLocationIds.length);
    const outside = countActive(this.selectedVehicleSeverities.length, this.viewUnreadOnly, this.searchForm.value.nameOrVinSearch);
    return { locations, alert, case: cases, total: locations + alert + cases + outside };
  }

  // In drawer mode severity is a drawer pill, so alertFilterTemplate has nothing to open.
  get filterTemplateEmpty(): boolean {
    return this.filterDrawerEnabled;
  }

  private saveFilterState(): void {
    const savedPathName = location.pathname + '/diagnostics/alert';
    const currentLists = this.getPagedList(savedPathName);
    const existingList = currentLists.find(x => x.url === savedPathName);
    const updatedList = Object.assign(new PagedList(savedPathName), existingList ?? {});
    updatedList.pageIndex = 0;
    // Keep the filter card open — otherwise the round-tripped PagedList resets it to the
    // default (false) and the table collapses the panel on the first filter.
    updatedList.showFilterFields = true;
    updatedList.customFilters = JSON.stringify({ ...this.facets(), groupedView: this.groupedView });
    this.store.dispatch(new UpdatePagedList(updatedList));
  }

  private loadTenantFeaturesAndUsers(): void {
    this.featuresSub$.next();
    // Features/users/dropdowns load independent of currentUser hydration — only the
    // grid/summary fetches gate on the saved My View (see initializeTenantData + the
    // getData/getSummary guards). Coupling these to currentUser previously stalled the
    // whole page (and re-ran on every currentUser re-emit) if the user never hydrated.
    combineLatest([
      this.getTenantFeatures$,
      this.currentTenantUsers$
    ]).pipe(
      takeUntil(this.featuresSub$),
      takeUntil(this.destroy$)
    ).subscribe(([features, users]) => {
      this.applyTenantFeatures(features);
      this.tenantUsers = this.getUsers(users, true);
      this.realTenantUsers = this.getUsers(users);
      this.assignedUserOptions = this.tenantUsers.map((u: any) => ({ value: u.id, label: u.fullName }));
      this.lastUpdatedByOptions = this.realTenantUsers.map((u: any) => ({ value: u.id, label: u.fullName }));
    });
  }

  private applyTenantFeatures(features: FeatureSettings): void {
    // Release the gate even with no settings so the grid still loads (flat default).
    if (!features?.settings) {
      this.featuresApplied$.next();
      return;
    }

    this.hasCaseCreationEnabled = features.settings.find(
      s => s.name === TenantFeatures.WorkOrderCreation
    )?.isEnabled ?? false;

    this.hasGroupedAlertsEnabled = features.settings.find(
      s => s.name === TenantFeatures.GroupedAlerts
    )?.isEnabled ?? false;

    this.hasAlertModalEnabled = features.settings.find(
      s => s.name === TenantFeatures.DiagnosticAlertModal
    )?.isEnabled ?? false;

    this.hideSeverityFilter = features.settings.find(
      s => s.name === TenantFeatures.HideAlertSeverityFilter
    )?.isEnabled ?? false;

    this.savedFiltersEnabled = !this.isNested && isFeatureEnabled(features, TenantFeatures.SavedFilters);
    this.filterDrawerEnabled = !this.isNested && isFeatureEnabled(features, TenantFeatures.FilterDrawer);

    // Grouped view is forced off when the flag is off; otherwise honor the
    // persisted mode, defaulting to grouped on first visit.
    if (!this.hasGroupedAlertsEnabled) {
      this.groupedView = false;
    } else if (!this.groupedViewPersisted) {
      this.groupedView = true;
    }
    this.syncGroupedToggleFunction();
    this.rebuildGridColumns();
    // Mode settled — release the initial-load gate (toggles handle their own refetch).
    this.featuresApplied$.next();
  }

  // Rebuilds gridDisplayColumns from the case-creation gating plus the
  // grouped-only count column. Called whenever either input changes.
  private rebuildGridColumns(): void {
    const base = this.hasCaseCreationEnabled
      ? [...this.allGridDisplayColumns]
      : this.allGridDisplayColumns.filter(c => !this.caseColumns.has(c.property));
    this.gridDisplayColumns = this.groupedView
      ? [...base, this.groupExpandColumn]
      : base;
  }

  private syncGroupedToggleFunction(): void {
    const exists = this.tableFunctions.some(f => f.action === 'toggleGrouped');
    if (this.hasGroupedAlertsEnabled && !exists) {
      this.tableFunctions = [
        ...this.tableFunctions,
        { display: '@toggleGrouped', action: 'toggleGrouped', matIcon: 'workspaces' }
      ];
    } else if (!this.hasGroupedAlertsEnabled && exists) {
      this.tableFunctions = this.tableFunctions.filter(f => f.action !== 'toggleGrouped');
    }
    const fn = this.tableFunctions.find(f => f.action === 'toggleGrouped');
    if (fn) {
      fn.display = this.groupedView ? '@showFlatList' : '@showGroupedList';
      this.tableFunctions = [...this.tableFunctions];
    }
    this.syncExpandAllFunctions();
  }

  // dynamic-table.ngOnChanges pushes 'expandAll'/'collapseAll' into our
  // tableFunctions array (shared by reference) whenever expandTemplate is
  // non-null — i.e. in grouped mode. These are grouped-only toolbar buttons,
  // so strip them whenever we're in flat mode; otherwise a grouped→flat toggle
  // leaves them lingering in the flat toolbar (regression to today's flat list).
  private syncExpandAllFunctions(): void {
    if (this.groupedView) return;
    const hasExpandFns = this.tableFunctions.some(
      f => f.action === 'expandAll' || f.action === 'collapseAll'
    );
    if (hasExpandFns) {
      this.tableFunctions = this.tableFunctions.filter(
        f => f.action !== 'expandAll' && f.action !== 'collapseAll'
      );
    }
  }

  private readonly caseColumns = new Set([
    'caseButton',
    'updateDateTimeUtc',
    'caseUserStatus',
    'vehicleSeverity',
    'lastUpdatedBy',
    'assignedTo',
    'assignedToLocation'
  ]);

  ngOnDestroy(): void {
    this.featuresSub$.next();
    this.featuresSub$.complete();
    this.statusesLoaded$.complete();
    this.featuresApplied$.complete();
    this.dataRequest$.complete();
    this.summaryRequest$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
  getUsers(users: TenantUser[], includeUnassigned: boolean = false): any[] {
    const translatedUnassigned = this.translate.instant('@unassigned');
    const sortedRealUsers = users.map(u => ({
      ...u,
      fullName: `${u.firstName || ''} ${u.lastName || ''}`.trim()
    })).sort((a, b) => a.fullName.localeCompare(b.fullName));
    if (includeUnassigned) {
      const unassignedOption = {
        ...this.unassignedUser,
        fullName: translatedUnassigned
      };
      sortedRealUsers.unshift(unassignedOption);
    }
    return sortedRealUsers;
  }
  loadAlertRules(): void {
    this.userService.getUserAlertRuleNames().subscribe(res => {
      this.alertRules = res.sort((a, b) => a.subject.localeCompare(b.subject));
      this.alertRuleOptions = this.alertRules.map(r => ({ value: r.id, label: r.subject }));
    });
  }

  loadLocationGroups(): void {
    this.userService.getUserLocationGroups().subscribe(allLocations => {
      this.locationGroups = allLocations.sort((a, b) => a.name.localeCompare(b.name));
      this.assignedLocationOptions = this.locationGroups.map((l: any) => ({ value: l.id, label: l.name }));
      this.homeLocationOptions = this.assignedLocationOptions;
    });
  }

  loadShopGroups(): void {
    this.userService.getUserShopGroups().subscribe(shops => {
      this.shopLocationOptions = [
        { value: this.overTheRoadValue, label: this.translate.instant('@otr') },
        ...shops
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((s: any) => ({ value: s.id, label: s.name }))
      ];
    });
  }

  loadStatuses(): void {
    this.alertService.getCaseUserStatuses().subscribe(res => {
      this.caseStatuses = res.map((s: CaseUserStatus) => ({
        label: s.name,
        value: s.id,
        isTerminal: s.isTerminal,
        systemStatus: s.systemStatus ?? null,
        count: 0,
        baselineCount: 0
      }));
      ({ lead: this.caseStatusLead, groups: this.caseStatusGroups } = groupCaseStatuses(this.caseStatuses));
      this.caseStatuses.unshift({ label: '@notStarted', value: 0, isTerminal: false, systemStatus: null, count: 0, baselineCount: 0 });
      this.statusesLoaded$.next();
    });
  }

  getSummary(): void {
    // Same currentUser guard as getData(): the summary also sends alertTags, so an early
    // ungated call (e.g. from a refreshView() before hydration) would count the whole tenant.
    if (!this.currentUser) return;
    this.summaryRequest$.next();
  }

  // Builds the summary request (and its dependent baseline call) as one cancelable stream.
  private fetchSummary$(): Observable<any> {
    // Mirror getData's gate: only count by group once the flag is confirmed enabled, so the
    // chips and the grid agree on which counting mode they're in.
    const useGrouped = this.groupedView && this.hasGroupedAlertsEnabled;
    const p = this.buildCommonParams();
    return this.alertService.getAlertSummary(
      this.selectedAlertSeverities,
      this.selectedVehicleSeverities,
      p.filteredStatuses,
      this.selectedLastUpdatedByUserIds,
      p.filteredAssignedUserIds,
      this.selectedAssignedLocationIds,
      this.selectedAlertRuleIds,
      p.vinsParam,
      p.filterByUnassigned,
      p.filterByNoCase,
      this.viewUnreadOnly || undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      useGrouped,
      this.currentUser?.alertTags,
      this.searchForm.value.nameOrVinSearch || undefined,
      this.selectedLocationGroupIds.length ? this.selectedLocationGroupIds : undefined,
      p.shopGroupIds,
      p.overTheRoad,
      p.createdFrom,
      p.createdTo)
      .pipe(switchMap(res => {
        this.totalFilteredCount = Object.values(res.severityCounts || {}).reduce((acc: number, val: any) => acc + val, 0) as number;
        // In grouped mode the paginator length is owned by the GROUP count from
        // /alert/grouped; the grouped summary now also counts groups, so leave the
        // paginator to its single source of truth and don't set it from here.
        if (this.isFiltered() && !useGrouped) {
          this.length = this.totalFilteredCount;
        }

        if (!this.isFiltered()) {
          this.baselineSummary = JSON.parse(JSON.stringify(res));
        }

        if (!this.baselineSummary) {
          // Baseline must match the grouped/flat mode of `res` so the chip's count/baseline
          // pair are both group counts (or both alert counts).
          return this.alertService.getBaselineSummary(useGrouped, this.currentUser?.alertTags).pipe(
            tap(baselineRes => {
              this.baselineSummary = baselineRes;
              this.applyCountsFromSummary(res);
            }));
        }
        this.applyCountsFromSummary(res);
        return of(res);
      }));
  }

  private applyCountsFromSummary(res: any): void {
    this.alertSeverities.forEach(s => {
      s.count = res.severityCounts[s.value] || 0;
      s.baselineCount = this.baselineSummary.severityCounts[s.value] || 0;
    });

    this.vehicleSeverities.forEach(v => {
      v.count = 0;
      v.baselineCount = 0;
    });
    this.processVehicleBuckets(res.vehicleSeverityCounts, 'count');
    this.processVehicleBuckets(this.baselineSummary.vehicleSeverityCounts, 'baselineCount');

    this.caseStatuses.forEach(w => {
      w.count = res.statusCounts ? res.statusCounts[w.value] || 0 : 0;
      w.baselineCount = this.baselineSummary.statusCounts ? this.baselineSummary.statusCounts[w.value] || 0 : 0;
    });
  }

  private processVehicleBuckets(sourceCounts: any, targetProp: 'count' | 'baselineCount'): void {
    if (!sourceCounts) return;
    Object.entries(sourceCounts).forEach(([rawKey, count]) => {
      const rawValue = parseFloat(rawKey);
      const targetBucket = this.vehicleSeverities
        .sort((a, b) => (b.value as number) - (a.value as number))
        .find(v => rawValue >= (v.value as number));

      if (targetBucket) {
        (targetBucket as any)[targetProp] += (count as number);
      }
    });
  }

  filterByStatus(selectedOptions: any[]): void {
    this.selectedStatuses = selectedOptions.map(o => o.value);
    this.refreshView();
  }

  filterByAlertSeverity(severities: string[]): void {
    this.selectedAlertSeverities = severities;
    this.refreshView();
  }

  filterByVehicleSeverity(values: any[]): void {
    this.selectedVehicleSeverities = values;
    this.refreshView();
  }

  filterByLastUpdatedUser($event: any): void {
    this.selectedLastUpdatedByUserIds = $event;
    this.refreshView();
  }

  filterByAlertRule($event: any): void {
    this.selectedAlertRuleIds = $event;
    this.refreshView();
  }

  filterByAssignedLocation($event: any): void {
    this.selectedAssignedLocationIds = $event;
    this.refreshView();
  }

  filterByLocationGroup($event: any): void {
    this.selectedLocationGroupIds = $event;
    this.refreshView();
  }

  filterByShopLocation($event: any): void {
    this.selectedShopLocationIds = $event;
    this.refreshView();
  }

  filterByAssignedUser($event: any): void {
    this.selectedAssignedUserIds = $event;
    this.refreshView();
  }

  filterByUnreadOnly(checked: boolean): void {
    this.viewUnreadOnly = checked;
    this.syncUnreadToggleFunction();
    this.refreshView();
  }

  clearAllFilters(): void {
    this.selectedStatuses = [];
    this.selectedAlertSeverities = [];
    this.selectedVehicleSeverities = [];
    this.selectedLastUpdatedByUserIds = [];
    this.selectedAssignedUserIds = [];
    this.selectedAssignedLocationIds = [];
    this.selectedLocationGroupIds = [];
    this.selectedShopLocationIds = [];
    this.selectedAlertRuleIds = [];
    this.viewUnreadOnly = false;
    this.clearReactiveForm();
    this.clearDateRange();
    this.syncUnreadToggleFunction();
    if (!this.vin) {
      this.filterVin = '';
    }
    this.refreshView();
  }

  private clearDateRange(): void {
    this.createdRange = EMPTY_DATE_RANGE;
  }

  filterByCreatedDate(value: DateRangeFilterValue): void {
    this.createdRange = value;
    this.refreshView();
  }

  clearReactiveForm(): void {
    this.searchForm.reset({ nameOrVinSearch: '' }, { emitEvent: false });
  }

  get displaySeverities() {
    return this.alertSeverities.filter(s => s.value !== 'None');
  }

  // Dropdown options mirror the (hidden) severity cards — same severities, minus None.
  get alertSeverityOptions(): FilterDropdownOption[] {
    return this.displaySeverities.map(s => ({ value: s.value, label: this.translate.instant(s.label) }));
  }

  get primaryStatuses(): any[] {
    return this.caseStatuses.filter(s => !this.quickFilterStatusLabels.has(s.label));
  }

  get quickFilterStatuses(): any[] {
    return this.caseStatuses.filter(s => this.quickFilterStatusLabels.has(s.label));
  }

  get hasMoreFiltersActive(): boolean {
    return this.selectedVehicleSeverities.length > 0 ||
      this.selectedLastUpdatedByUserIds.length > 0 ||
      this.selectedAssignedLocationIds.length > 0 ||
      this.selectedAssignedUserIds.length > 0;
  }

  // The More Filters panel is the Case Filters region — nothing to reveal without cases.
  get hasMoreFilters(): boolean {
    return this.hasCaseCreationEnabled;
  }

  private get hasDateRange(): boolean {
    return !!this.createdRange.from || !!this.createdRange.to;
  }

  get activeFilterTags(): { label: string; remove: () => void }[] {
    const tags: { label: string; remove: () => void }[] = [];

    for (const sev of this.selectedAlertSeverities) {
      tags.push({
        label: `${this.translate.instant('@severity')}: ${sev}`,
        remove: () => { this.selectedAlertSeverities = this.selectedAlertSeverities.filter(s => s !== sev); this.refreshView(); }
      });
    }

    for (const status of this.selectedStatuses) {
      const statusObj = this.caseStatuses.find(s => s.value === status);
      const label = statusObj ? this.translate.instant(statusObj.label) : String(status);
      tags.push({
        label: `${this.translate.instant('@caseStatus')}: ${label}`,
        remove: () => { this.selectedStatuses = this.selectedStatuses.filter(s => s !== status); this.refreshView(); }
      });
    }

    for (const vs of this.selectedVehicleSeverities) {
      const vsLabel = this.getLabelForVehicleSeverity(vs);
      tags.push({
        label: `${this.translate.instant('@vehicleSeverity')}: ${this.translate.instant(vsLabel)}`,
        remove: () => { this.selectedVehicleSeverities = this.selectedVehicleSeverities.filter(v => v !== vs); this.refreshView(); }
      });
    }

    for (const id of this.selectedLastUpdatedByUserIds) {
      const user = this.realTenantUsers.find(u => u.id === id);
      const name = user ? `${user.firstName} ${user.lastName}`.trim() : this.translate.instant('@unknown');
      tags.push({
        label: `${this.translate.instant('@lastUpdatedBy')}: ${name}`,
        remove: () => { this.selectedLastUpdatedByUserIds = this.selectedLastUpdatedByUserIds.filter(u => u !== id); this.refreshView(); }
      });
    }

    for (const id of this.selectedAssignedUserIds) {
      const user = this.tenantUsers.find(u => u.id === id);
      const name = id === '0' ? this.translate.instant('@unassigned') : (user ? `${user.firstName} ${user.lastName}`.trim() : this.translate.instant('@unknown'));
      tags.push({
        label: `${this.translate.instant('@assignedTo')}: ${name}`,
        remove: () => { this.selectedAssignedUserIds = this.selectedAssignedUserIds.filter(u => u !== id); this.refreshView(); }
      });
    }

    for (const id of this.selectedAssignedLocationIds) {
      const loc = this.locationGroups.find((l: any) => l.id === id);
      const name = loc ? loc.name : this.translate.instant('@unknown');
      tags.push({
        label: `${this.translate.instant('@assignedToLocation')}: ${name}`,
        remove: () => { this.selectedAssignedLocationIds = this.selectedAssignedLocationIds.filter(l => l !== id); this.refreshView(); }
      });
    }

    for (const id of this.selectedLocationGroupIds) {
      const loc = this.homeLocationOptions.find(l => l.value === id);
      const name = loc ? loc.label : this.translate.instant('@unknown');
      tags.push({
        label: `${this.translate.instant('@homeLocations')}: ${name}`,
        remove: () => { this.selectedLocationGroupIds = this.selectedLocationGroupIds.filter(l => l !== id); this.refreshView(); }
      });
    }

    for (const id of this.selectedShopLocationIds) {
      const opt = this.shopLocationOptions.find(o => o.value === id);
      const name = opt ? opt.label : this.translate.instant('@unknown');
      tags.push({
        label: `${this.translate.instant('@shopLocations')}: ${name}`,
        remove: () => { this.selectedShopLocationIds = this.selectedShopLocationIds.filter(s => s !== id); this.refreshView(); }
      });
    }

    if (this.hasDateRange) {
      tags.push({
        label: `${this.translate.instant('@createdDate')}: ${this.dateRangeLabel}`,
        remove: () => { this.clearDateRange(); this.refreshView(); }
      });
    }

    for (const id of this.selectedAlertRuleIds) {
      const rule = this.alertRules.find((r: any) => r.id === id);
      const name = rule ? rule.subject : this.translate.instant('@unknown');
      tags.push({
        label: `${this.translate.instant('@alertName')}: ${name}`,
        remove: () => { this.selectedAlertRuleIds = this.selectedAlertRuleIds.filter(r => r !== id); this.refreshView(); }
      });
    }

    if (this.viewUnreadOnly) {
      tags.push({
        label: this.translate.instant('@unreadOnly'),
        remove: () => { this.viewUnreadOnly = false; this.syncUnreadToggleFunction(); this.refreshView(); }
      });
    }

    const searchValues = this.searchForm.value;
    if (searchValues.nameOrVinSearch) {
      tags.push({
        label: `${this.translate.instant('@assetNameOrVin')}: "${searchValues.nameOrVinSearch}"`,
        remove: () => { this.searchForm.patchValue({ nameOrVinSearch: '' }); }
      });
    }

    return tags;
  }

  toggleSeverity(value: string): void {
    const idx = this.selectedAlertSeverities.indexOf(value);
    if (idx >= 0) {
      this.selectedAlertSeverities = this.selectedAlertSeverities.filter(v => v !== value);
    } else {
      this.selectedAlertSeverities = [...this.selectedAlertSeverities, value];
    }
    this.refreshView();
  }

  toggleStatus(value: number): void {
    const idx = this.selectedStatuses.indexOf(value);
    if (idx >= 0) {
      this.selectedStatuses = this.selectedStatuses.filter(v => v !== value);
    } else {
      this.selectedStatuses = [...this.selectedStatuses, value];
    }
    this.refreshView();
  }

  // "All Active" / "All Closed" super-filters: group shortcuts over the individual status chips,
  // grouped by the canonical terminal flag (closed = terminal, active = everything else).
  get closedStatusValues(): number[] {
    return this.caseStatuses.filter(s => s.isTerminal).map(s => s.value);
  }

  get activeStatusValues(): number[] {
    return this.caseStatuses.filter(s => !s.isTerminal).map(s => s.value);
  }

  private isGroupSelected(values: number[]): boolean {
    return values.length > 0 && values.every(v => this.selectedStatuses.includes(v));
  }

  get isActiveGroupSelected(): boolean {
    return this.isGroupSelected(this.activeStatusValues);
  }

  get isClosedGroupSelected(): boolean {
    return this.isGroupSelected(this.closedStatusValues);
  }

  toggleStatusGroup(values: number[]): void {
    if (values.length === 0) return;
    // Mutually exclusive super-filters: selecting a group replaces the current selection with it
    // (so All Active then All Closed deselects All Active); clicking the selected group clears it.
    this.selectedStatuses = this.isGroupSelected(values) ? [] : [...values];
    this.refreshView();
  }

  isCaseGroupSelected(values: number[]): boolean {
    return this.isGroupSelected(values);
  }

  // Unlike All Active / All Closed, a drawer group adds to the selection, so groups combine.
  toggleCaseGroup(values: number[]): void {
    this.selectedStatuses = this.isGroupSelected(values)
      ? this.selectedStatuses.filter(v => !values.includes(v))
      : [...this.selectedStatuses, ...values.filter(v => !this.selectedStatuses.includes(v))];
    this.refreshView();
  }

  private groupCount(values: number[], prop: 'count' | 'baselineCount'): number {
    return this.caseStatuses
      .filter(s => values.includes(s.value))
      .reduce((sum, s) => sum + (s[prop] || 0), 0);
  }

  get activeGroupCount(): number {
    return this.groupCount(this.activeStatusValues, 'count');
  }

  get activeGroupBaselineCount(): number {
    return this.groupCount(this.activeStatusValues, 'baselineCount');
  }

  get closedGroupCount(): number {
    return this.groupCount(this.closedStatusValues, 'count');
  }

  get closedGroupBaselineCount(): number {
    return this.groupCount(this.closedStatusValues, 'baselineCount');
  }

  clearSearch(): void {
    this.clearReactiveForm();
    this.refreshView();
  }

  private refreshView(): void {
    this.currentOffset = 0;
    this.saveFilterState();
    this.getSummary();
    this.getData();
  }

  isFiltered(): boolean {
    return this.drawerCounts.total > 0;
  }

  private resolveStructuredSort(matSortActive?: string, matSortDirection?: string): void {
    if (!matSortActive) return;
    const col = this.gridDisplayColumns.find(c => c.property === matSortActive);
    if (col?.sortProperty) {
      this.currentSortBy = col.sortProperty;
      this.currentSortDescending = matSortDirection?.toLowerCase() !== 'asc';
    }
  }

  getData(): void {
    // Guard every caller (incl. debounced search before hydration): without a hydrated user the
    // allowedTags scope drops and we'd query the whole tenant.
    if (!this.currentUser) return;
    this.dataRequest$.next();
  }

  private fetchFlatData$(): Observable<any> {
    const p = this.buildCommonParams();
    return this.alertService.getAlertData(
      this.currentOffset, this.currentLimit,
      this.selectedAlertSeverities, this.selectedVehicleSeverities, p.filteredStatuses,
      this.selectedLastUpdatedByUserIds,
      p.filteredAssignedUserIds,
      this.selectedAssignedLocationIds,
      this.selectedAlertRuleIds,
      p.vinsParam,
      p.filterByUnassigned,
      p.filterByNoCase,
      this.viewUnreadOnly ? true : undefined,
      this.currentSortBy,
      this.currentSortDescending,
      undefined,
      undefined,
      undefined,
      undefined,
      this.currentUser?.alertTags,
      this.searchForm.value.nameOrVinSearch || undefined,
      this.selectedLocationGroupIds.length ? this.selectedLocationGroupIds : undefined,
      p.shopGroupIds,
      p.overTheRoad,
      p.createdFrom,
      p.createdTo)
      .pipe(tap(res => {
        res.documents = res.documents.map((alert: any) => this.mapAlert(alert));
        this.dataSource = new MatTableDataSource(res.documents);
        this.length = res.count;
      }));
  }

  private fetchGroupedData$(): Observable<any> {
    // Remember which groups are open so we can re-open them after the reload; the
    // members come back inline with each group, so there's no per-group re-fetch.
    const previouslyExpanded = new Set(
      (this.dataSource?.data || [])
        .filter((r: any) => r.expanded && r.groupKey)
        .map((r: any) => r.groupKey));
    const p = this.buildCommonParams();
    return this.alertService.getGroupedAlertData(
      this.currentOffset, this.currentLimit,
      this.selectedAlertSeverities, this.selectedVehicleSeverities, p.filteredStatuses,
      this.selectedLastUpdatedByUserIds,
      p.filteredAssignedUserIds,
      this.selectedAssignedLocationIds,
      this.selectedAlertRuleIds,
      p.vinsParam,
      p.filterByUnassigned,
      p.filterByNoCase,
      this.viewUnreadOnly ? true : undefined,
      this.currentSortBy,
      this.currentSortDescending,
      undefined,
      undefined,
      undefined,
      undefined,
      this.currentUser?.alertTags,
      this.searchForm.value.nameOrVinSearch || undefined,
      this.selectedLocationGroupIds.length ? this.selectedLocationGroupIds : undefined,
      p.shopGroupIds,
      p.overTheRoad,
      p.createdFrom,
      p.createdTo)
      .pipe(tap(res => {
        this.groupRows = res.documents.map((alert: any) => {
          const mapped = this.mapAlert(alert);
          const count = alert.count ?? 1;
          // "+N" = sibling alerts beyond the representative row.
          mapped.groupCountLabel = count > 1 ? `+${count - 1}` : '';
          mapped.groupKey = this.groupKey(mapped);
          mapped.count = count;
          // Re-open groups that were expanded before the reload (members are inline).
          mapped.expanded = previouslyExpanded.has(mapped.groupKey);
          mapped.expandCell = { template: this.groupExpandCellTemplate, inputs: { row: mapped } };
          // Members come back inline with the group (latest first, representative included).
          // Drop the representative — it's the parent row — and map the rest as child rows
          // so each renders identically to a flat row.
          mapped.children = (alert.members || [])
            .filter((member: any) => member.id !== mapped.id)
            .map((member: any, idx: number) => {
              const child = this.mapAlert(member);
              child._isGroupChild = true;
              child._parentGroupKey = mapped.groupKey;
              // Children never show the count "+N" or the expand chevron (no expandCell).
              child.groupCountLabel = '';
              // Asset Name + Subject are identical to the parent (the group keys on
              // rule.id + vin), so instead of repeating them, relabel the row as an earlier
              // firing of the same condition. Members arrive newest-first, so idx 0 is the
              // most recent prior firing (directly beneath the parent) = "Previous occurrence
              // 1", incrementing into the past — matching the table's newest-first sort. The
              // label rides in the Asset Name cell (Subject is already blanked on children).
              child.vehicleName = this.translate.instant('@previousOccurrence', { n: idx + 1 });
              return child;
            });
          // No silent cap: members are capped server-side, so a very large group can render
          // fewer children than its "+N" — surface that in logs.
          if (count - 1 > mapped.children.length) {
            console.warn(`Grouped alerts: group ${mapped.groupKey} shows ${mapped.children.length} of ${count - 1} sibling alerts (server cap).`);
          }
          return mapped;
        });
        // Reuse the same MatTableDataSource instance across toggles; only create
        // one the first time (or after a flat→grouped swap reset it).
        if (!this.dataSource) {
          this.dataSource = new MatTableDataSource<Alert>([]);
        }
        // GROUP count — never overwritten by getSummary in grouped mode.
        this.length = res.count;
        this.rebuildGroupedRows();
      }));
  }

  // Flattens groupRows into dataSource.data: each parent followed by its inline
  // children when the parent is expanded. Reassigns .data (not a new
  // MatTableDataSource) so the table re-renders without losing instance state.
  private rebuildGroupedRows(): void {
    const flat: any[] = [];
    this.groupRows.forEach((parent: any) => {
      flat.push(parent);
      if (parent.expanded) {
        (parent.children ?? []).forEach((child: any) => flat.push(child));
      }
    });
    if (this.dataSource) {
      this.dataSource.data = flat;
    } else {
      this.dataSource = new MatTableDataSource<Alert>(flat);
    }
  }

  // Toggles a representative row's expanded state. Children are already loaded inline
  // (row.children from the grouped query), so this just flips the flag and re-renders.
  toggleGroupExpand(row: any): void {
    row.expanded = !row.expanded;
    this.rebuildGroupedRows();
  }

  // Builds the assigned/status/vin/location params shared by getData and getGroupedData.
  private buildCommonParams(): {
    filteredAssignedUserIds: string[];
    filterByUnassigned: boolean;
    filteredStatuses: number[];
    filterByNoCase: boolean;
    vinsParam: string[] | undefined;
    shopGroupIds: string[] | undefined;
    overTheRoad: boolean | undefined;
    createdFrom: string | undefined;
    createdTo: string | undefined;
  } {
    // filter out 'unassigned' pseudo-id — send as filterByUnassigned flag instead
    const filteredAssignedUserIds = this.selectedAssignedUserIds.filter(id => id !== this.unassignedUser.id);
    const filterByUnassigned = this.selectedAssignedUserIds.includes(this.unassignedUser.id);
    // filter out 'not started' (value 0) — send as filterByNoCase flag instead
    const filteredStatuses = this.selectedStatuses.filter(s => s !== 0);
    const filterByNoCase = this.selectedStatuses.includes(0);
    const vinsParam = this.filterVin ? [this.filterVin] : undefined;
    // filter out the OTR pseudo-value — send as overTheRoad flag instead
    const shopIds = this.selectedShopLocationIds.filter(id => id !== this.overTheRoadValue);
    const shopGroupIds = shopIds.length ? shopIds : undefined;
    const overTheRoad = this.selectedShopLocationIds.includes(this.overTheRoadValue) || undefined;
    const from = this.createdRange.from;
    const to = this.createdRange.to;
    const createdFrom = from ? new Date(from).toISOString() : undefined;
    // "to" day is inclusive: send its end-of-day instant.
    const createdTo = to ? new Date(new Date(to).setHours(23, 59, 59, 999)).toISOString() : undefined;
    return {
      filteredAssignedUserIds, filterByUnassigned, filteredStatuses, filterByNoCase,
      vinsParam, shopGroupIds, overTheRoad, createdFrom, createdTo
    };
  }

  // Per-alert display transform, shared by representative and child rows so a
  // child row renders identically to a flat row.
  private mapAlert(alert: any): any {
    if (alert.vehicleState?.dashboard?.indicators) {
      alert.vehicleState.dashboard.indicators.forEach((lamp: any) => {
        alert.vehicleState.dashboard[lamp.name] = lamp.enabled;
      });
    }

    const locationGroups = alert.groups.filter(y => y.values['LocationGroup'] === 'True');
    alert.locationGroups = this.formatGroups(locationGroups);
    alert.displayGroups = this.formatGroups(alert.groups);

    const shopGroup = findShopGroup(alert.groups);
    alert.vehicleLocation = shopGroup ? shopGroup.name : this.translate.instant('@otr');

    const rawVehicleSev = alert.vehicleState?.severities?.find(s => s.type === 'Vehicle')?.value || 0;
    alert.vehicleSeverity = this.mapToSeverityType(rawVehicleSev);
    alert.caseUserStatus = `${this.toCaseStatus(alert.statuses?.find(s => s.statusType === AlertStatusType.CaseUserStatus)?.status || 0)}`;

    alert.lastUpdatedBy = `${this.getUserNameForUserId(alert.statuses.find(s => s.statusType === AlertStatusType.CaseUserStatus && s.userId)?.userId || '')}`;
    alert.caseButton = this.getTableButton(alert);
    const assignedUsers = alert.cases.map(s => s.assignedUserId);
    let assignedUserNames: string[] = [];
    assignedUsers.forEach((userId: string) => {
      assignedUserNames.push(this.getUserNameForUserId(userId));
    });
    alert.assignedTo = assignedUserNames.join(', ');
    const assignedLocations = alert.cases.map(s => s.assignedLocationId);
    let assignedLocationNames: string[] = [];
    assignedLocations.forEach((locationId: string) => {
      assignedLocationNames.push(this.getLocationNameForLocationId(locationId));
    });
    alert.assignedToLocation = assignedLocationNames.join(', ');

    return alert;
  }

  // Group identity: alertRule.id + vin (matches the backend grouping key).
  private groupKey(alert: any): string {
    return `${alert.alertRule?.id ?? ''}|${alert.vehicleState?.vin ?? ''}`;
  }

  private getUserNameForUserId(userId: string): string {
    if (!userId || userId === this.unassignedUser.id) {
      return this.translate.instant('@unassigned');
    }
    const user = this.tenantUsers.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}`.trim() : this.translate.instant('@unknown');
  }

  private getLocationNameForLocationId(locationId: string): string {
    if (!locationId) return this.translate.instant('@unknown');
    const location = this.locationGroups.find(l => l.id === locationId);
    return location ? location.name : this.translate.instant('@unknown');
  }

  toCaseStatus(status: number): string {
    const label = this.caseStatuses.find(s => s.value === status)?.label || '@notStarted';
    return this.translate.instant(label);
  }

  private mapToSeverityType(value: number): SeverityTypes {
    if (value >= SeverityTypes.Critical) return SeverityTypes.Critical;
    if (value >= SeverityTypes.High) return SeverityTypes.High;
    if (value >= SeverityTypes.Medium) return SeverityTypes.Medium;
    if (value >= SeverityTypes.Low) return SeverityTypes.Low;
    return SeverityTypes.None;
  }

  private formatGroups(groups: any[]): string[] {
    return Array.from(groups || [], g => g.name !== '' ? ' ' + g.name : '');
  }

  pageChangedEvent(event: any): void {
    this.currentLimit = event.pageSize;
    this.currentOffset = event.pageSize * event.pageIndex;
    this.getData();
  }

  sortDataEvent(event: { sort: string; matSortActive?: string; matSortDirection?: string }): void {
    this.resolveStructuredSort(event.matSortActive, event.matSortDirection);
    this.currentOffset = 0;
    this.getData();
  }

  selectRowEvent(req: any): void {
    if (!this.hasAlertModalEnabled) {
      this.spaRoutingService.navigateToAlert(req.row.id);
      return;
    }
    this.openAlertDialog(req.row);
  }

  // Quick-view dialog shared with Service Center; seeds an AlertSummary so the
  // dialog can fetch the full alert itself. count only exists on grouped
  // representative rows, and createDateTimeUtc is the firing time (a grouped
  // representative is the group's latest alert, so it doubles as last-fired).
  private openAlertDialog(alert: any): void {
    if (!alert?.id) return;
    this.setActiveRow(alert);
    this.triage.open(alertsTableDialogData(alert, this.dataSource?.data ?? [])).pipe(take(1)).subscribe(result => {
      if (result?.action === 'viewCase' && result.alert) {
        this.openCaseDrawer(result.alert);
      } else {
        this.setActiveRow(null);
      }
    });
  }

  private openAlertFromUrl(): void {
    if (!this.hasAlertModalEnabled || !this.dataLoaded) return;
    this.triage.openLinked(id => (this.dataSource?.data ?? []).find(r => r?.id === id), alert => alert, row => this.openAlertDialog(row));
  }

  // Required by DynamicTableInterface; no longer wired to the init 'refresh' (see processFunction).
  refreshDataEvent(event: any): void {
    this.currentLimit = event.pageSize;
    this.currentOffset = event.pageSize * event.pageIndex;
    this.resolveStructuredSort(event.matSortActive, event.matSortDirection);
    this.getData();
  }

  processFunction(event: any): void {
    switch (event.action) {
      // Row clicks open the alert — parents via their representative alert,
      // children (Alert rows) via their own id. With the DiagnosticAlertModal
      // flag on this opens the quick-view dialog, otherwise it navigates to the
      // detail page. Expanding a group is driven only by the chevron button
      // column, never by a row click.
      case 'navigate': this.selectRowEvent(event); break;
      case 'page': this.pageChangedEvent(event); break;
      case 'sort': this.sortDataEvent(event); break;
      // Init 'refresh' ignored — the gate owns the initial load; honoring it would double-fetch.
      case 'show': this.toggleShowColumns(event.showAllColumns); break;
      case 'toggleUnreadOnly': this.filterByUnreadOnly(!this.viewUnreadOnly); break;
      case 'toggleGrouped': this.toggleGroupedView(); break;
    }
  }

  toggleGroupedView(): void {
    this.groupedView = !this.groupedView;
    this.groupedViewPersisted = true;
    this.currentOffset = 0;
    this.syncGroupedToggleFunction();
    this.rebuildGridColumns();
    this.saveFilterState();
    // The baseline is mode-specific (group vs alert counts); drop it before any fetch
    // so the chips re-baseline in the new mode.
    this.baselineSummary = null;
    this.getData();
    this.getSummary();
  }

  private toggleShowColumns(showAllColumn: boolean): void {
    this.gridDisplayColumns.forEach(col => {
      if (showAllColumn) {
        if (col.hidden === true) {
          col.hidden = false;
        }
      }
      else {
        if (col.hidden === false) {
          col.hidden = true;
        }
      }
    });
  }
  handleSearchKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  get filterSentence(): string {
    if (!this.isFiltered()) return '';

    const parts: string[] = [];
    const andStr = this.translate.instant('@and');
    const orStr = this.translate.instant('@or');

    if (this.filterVin && !this.vin) {
      parts.push(`${this.translate.instant('@vin')} ${this.filterVin}`);
    }

    if (this.selectedAlertSeverities.length > 0) {
      const values = this.joinWith(this.selectedAlertSeverities.map(s => this.translate.instant(s)), orStr);
      parts.push(this.translate.instant('@alertSeveritySegment', { values }));
    }

    if (this.selectedVehicleSeverities.length > 0) {
      const values = this.joinWith(this.selectedVehicleSeverities
        .map(v => this.getLabelForVehicleSeverity(v))
        .filter(label => !!label)
        .map(label => this.translate.instant(label)), orStr);
      if (values) parts.push(this.translate.instant('@vehicleSeveritySegment', { values }));
    }

    if (this.selectedStatuses.length > 0) {
      const statusValues = this.joinWith(this.selectedStatuses
        .map(s => this.getLabelForStatus(s))
        .filter(label => !!label)
        .map(label => this.translate.instant(label)), orStr);
      if (statusValues) parts.push(this.translate.instant('@statusSegment', { values: statusValues }));
    }

    if (this.selectedLastUpdatedByUserIds.length > 0) {
      const names = this.selectedLastUpdatedByUserIds.map(id => {
        const u = this.tenantUsers.find(user => user.id === id);
        return u ? `${u.firstName} ${u.lastName}`.trim() : this.translate.instant('@unknown');
      });
      const values = this.joinWith(names, orStr);
      parts.push(`${this.translate.instant('@lastUpdatedBy')} ${values}`);
    }

    if (this.selectedAssignedUserIds.length > 0) {
      const realUserIds = this.selectedAssignedUserIds.filter(id => id !== this.unassignedUser.id);
      const includesUnassigned = this.selectedAssignedUserIds.includes(this.unassignedUser.id);

      if (realUserIds.length > 0) {
        const names = realUserIds.map(id => {
          const u = this.tenantUsers.find(user => user.id === id);
          return u ? `${u.firstName} ${u.lastName}`.trim() : this.translate.instant('@unknown');
        });

        const assignedValues = this.joinWith(names, orStr);

        if (includesUnassigned) {
          parts.push(`${this.translate.instant('@assignedTo')} ${assignedValues} ${orStr} ${this.translate.instant('@unassigned')}`);
        }
        else {
          parts.push(`${this.translate.instant('@assignedTo')} ${assignedValues}`);
        }
      }
      else if (includesUnassigned) {
        parts.push(this.translate.instant('@noAssignedUser') || 'No Assigned User');
      }
    }

    if (this.selectedAssignedLocationIds.length > 0) {
      const names = this.selectedAssignedLocationIds.map(id => {
        const loc = this.locationGroups.find(l => l.id === id);
        return loc ? loc.name : this.translate.instant('@unknown');
      });
      const values = this.joinWith(names, orStr);
      parts.push(`${this.translate.instant('@location')} ${values}`);
    }

    if (this.selectedAlertRuleIds.length > 0) {
      const names = this.selectedAlertRuleIds.map(id => {
        const rule = this.alertRules.find(r => r.id === id);
        return rule ? rule.subject : this.translate.instant('@unknown');
      });
      const values = this.joinWith(names, orStr);
      parts.push(`${this.translate.instant('@alertRule')} ${values}`);
    }

    if (this.searchForm.value.nameOrVinSearch) {
      parts.push(`${this.translate.instant('@assetNameOrVin')} "${this.searchForm.value.nameOrVinSearch}"`);
    }

    if (this.selectedLocationGroupIds.length > 0) {
      const names = this.selectedLocationGroupIds.map(id => {
        const loc = this.homeLocationOptions.find(l => l.value === id);
        return loc ? loc.label : this.translate.instant('@unknown');
      });
      parts.push(`${this.translate.instant('@homeLocations')} ${this.joinWith(names, orStr)}`);
    }

    if (this.selectedShopLocationIds.length > 0) {
      const names = this.selectedShopLocationIds.map(id => {
        const opt = this.shopLocationOptions.find(o => o.value === id);
        return opt ? opt.label : this.translate.instant('@unknown');
      });
      parts.push(`${this.translate.instant('@shopLocations')} ${this.joinWith(names, orStr)}`);
    }

    if (this.hasDateRange) {
      parts.push(`${this.translate.instant('@createdDate')} ${this.dateRangeLabel}`);
    }

    if (this.viewUnreadOnly && parts.length === 0) {
      return this.translate.instant('@unreadOnly') + '.';
    }

    if (this.viewUnreadOnly) {
      parts.push(this.translate.instant('@unreadOnlyShort'));
    }

    const prefix = this.translate.instant('@alertsWith');
    const combinedSegments = this.joinWith(parts, andStr);

    return `${prefix} ${combinedSegments}.`;
  }

  private get dateRangeLabel(): string {
    const { from, to } = this.createdRange;
    const fmt = (d: Date) => d.toLocaleDateString();
    if (from && to) return `${fmt(from)} - ${fmt(to)}`;
    if (from) return `≥ ${fmt(from)}`;
    return to ? `≤ ${fmt(to)}` : '';
  }

  private joinWith(items: string[], separator: string): string {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} ${separator} ${items[1]}`;
    const lastItem = items.pop();
    return `${items.join(', ')}, ${separator} ${lastItem}`;
  }

  getLabelForVehicleSeverity(value: number): string {
    return this.vehicleSeverities.find(v => v.value === value)?.label || '';
  }

  getLabelForStatus(value: number): string {
    return this.caseStatuses.find(s => s.value === value)?.label || '';
  }
  private getTableButton(alert: Alert): TableButton {
    const hasCase = alert.cases && alert.cases.length > 0;

    let buttonIcon: string;
    let buttonClass: string;
    let buttonText: string;

    if (!hasCase) {
      buttonIcon = 'add_circle_outline';
      buttonClass = 'make-grey';
      buttonText = this.translate.instant('@createCase');
    } else {
      const statusValue = (alert.statuses as any[])?.find(s => s.statusType === AlertStatusType.CaseUserStatus)?.status ?? 0;
      const isClosed = this.caseStatuses.find(s => s.value === statusValue)?.isTerminal === true;

      buttonIcon = isClosed ? 'task_alt' : 'pending';
      buttonClass = isClosed ? 'make-green' : 'make-blue';
      buttonText = this.translate.instant('@viewCase');
    }

    // Without the right, a row with no case gets an empty cell. An existing case stays readable.
    const fixButton: TableButton = {
      display: hasCase || this.canCreateCase,
      clickAction: () => this.openCaseDrawer(alert),
      displayText: buttonText,
      icon: buttonIcon,
      customClass: buttonClass,
      hideTooltip: true
    };

    return fixButton;
  }
  private openCaseDrawer(alert: Alert): void {
    this.setActiveRow(alert);
    const caseDrawer = this.drawerService.open(AlertCaseComponent, [{ input: 'alert', value: alert }],
      { resizable: true, overlayClose: true, width: '40em' } as DrawerOptions
    );
    this.drawerService.guardClose(() => !caseDrawer.hasUnsavedEdits);

    // closed$ fires on every close path; close$ only fires on an in-drawer
    // action (not a backdrop/overlay close), so clear the highlight here.
    this.drawerService.closed$.pipe(take(1)).subscribe(() => this.setActiveRow(null));

    caseDrawer.close$.pipe(take(1)).subscribe((hasUpdates: boolean) => {
      this.drawerService.close();
      if (!hasUpdates) return;
      this.getData();
      this.getSummary();
    });
  }

  private setActiveRow(alert: Alert): void {
    if (this.activeRow) this.activeRow._activeRow = false;
    this.activeRow = alert;
    if (alert) alert._activeRow = true;
  }

}